import { forwardRef } from 'react'

import cx from 'classnames'

import {
  CommonAdd2Icon,
  CommonHorizontalArrow5Icon,
  FavoriteHomeIcon,
  FavoriteCompanyIcon,
  FavoriteFlagIcon,
  FavoriteSchoolIcon,
} from '@local/assets/icons'
import { CLICK_CODE } from '@local/utils'

import FlickingArea from '@web/components/FlickingArea/FlickingArea'
import Clickable from '@web/ui/components/Clickable'

import { Section, SectionHeader, SectionContent } from '../Section'
import styles from './FavoritesSection.module.scss'

const getIconByShortcutType = (shortcutType: string) => {
  if (shortcutType === 'HOME') return FavoriteHomeIcon

  if (shortcutType === 'OFFICE') return FavoriteCompanyIcon

  if (shortcutType === 'SCHOOL') return FavoriteSchoolIcon
  return FavoriteFlagIcon
}

export type FrequentPlaceShortCutType = 'HOME' | 'OFFICE' | 'SCHOOL' | 'ETC'

export interface FrequentPlacePrimitive {
  alias: string
  id: string
  name: string
  shortcutType: FrequentPlaceShortCutType
  type: string
  directionLandingUrl: {
    car: {
      appScheme?: string
      webUrl?: string
    }
  }
}

interface FavoriteItemProps extends Partial<FrequentPlacePrimitive> {
  isSaved: boolean
  onClick: () => void
  clickCode: string
}

const FavoriteItemPanel = ({
  name,
  alias,
  shortcutType,
  isSaved,
  onClick,
  clickCode,
  panelRef,
}: FavoriteItemProps & { panelRef: React.ForwardedRef<HTMLLIElement> }) => {
  const Icon = getIconByShortcutType(shortcutType || 'ETC')

  return (
    <li className={styles.list_item} ref={panelRef}>
      <Clickable
        className={cx(styles.list_item_link, { [styles.type_registered]: isSaved })}
        onClick={onClick}
        nClickCode={clickCode}
      >
        <Icon className={styles.icon} aria-hidden="true" />
        <span className={styles.name}>{alias || name}</span>
        {isSaved ? (
          <CommonHorizontalArrow5Icon className={styles.icon_arrow} aria-hidden="true" />
        ) : (
          <span className={styles.add_favorite}>
            등록
            <span className={styles.icon_wrapper}>
              <CommonAdd2Icon className={styles.add_icon} aria-hidden="true" />
            </span>
          </span>
        )}
      </Clickable>
    </li>
  )
}

const FavoriteItem = forwardRef<HTMLLIElement, FavoriteItemProps>((props, ref) => (
  <FavoriteItemPanel {...props} panelRef={ref} />
))
FavoriteItem.displayName = 'FavoriteItem'

interface UIFavoriteSectionProps {
  title: string
  isOnClickTitleValid: boolean
  home: FrequentPlacePrimitive
  office: FrequentPlacePrimitive
  school?: FrequentPlacePrimitive
  etcs: FrequentPlacePrimitive[]
  onClickTitle: () => void
  onClickEditMyHome: () => void
  onClickEditMyWorkplace: () => void
  onClickFrequentPlaceList: () => void
  onClickFindFastestPath: (item: FrequentPlacePrimitive) => void
}

const UIFavoriteSection = ({
  title,
  isOnClickTitleValid,
  home,
  office,
  school,
  etcs,
  onClickTitle,
  onClickEditMyHome,
  onClickEditMyWorkplace,
  onClickFrequentPlaceList,
  onClickFindFastestPath,
}: UIFavoriteSectionProps) => {
  return (
    <Section className={styles.favorites_section}>
      <SectionHeader title={title} clickHandler={isOnClickTitleValid ? onClickTitle : undefined} />
      <SectionContent>
        <FlickingArea
          options={{ cameraTag: 'ul', hideBeforeInit: true, nested: true }}
          arrowOption={{ hasGradient: true }}
          className={styles.flicking_area}
          alignLeftFocusCenter
        >
          <FavoriteItem
            id={home.id}
            key={home.id}
            name={home.name}
            alias={home.alias}
            shortcutType={home.shortcutType}
            isSaved={home.id !== 'home'}
            onClick={home.id !== 'home' ? () => onClickFindFastestPath(home) : onClickEditMyHome}
            clickCode={CLICK_CODE[home.id !== 'home' ? 'myfavplace.home' : 'myfavplace.addhome']}
          />
          {school ? (
            <FavoriteItem
              id={school.id}
              key={school.id}
              name={school.name}
              alias={school.alias}
              shortcutType={school.shortcutType}
              isSaved={true}
              onClick={() => onClickFindFastestPath(school)}
              clickCode={CLICK_CODE['myfavplace.school']}
            />
          ) : (
            <FavoriteItem
              id={office.id}
              key={office.id}
              name={office.name}
              alias={office.alias}
              shortcutType={office.shortcutType}
              isSaved={office.id !== 'office'}
              onClick={office.id !== 'office' ? () => onClickFindFastestPath(office) : onClickEditMyWorkplace}
              clickCode={CLICK_CODE[office.id !== 'office' ? 'myfavplace.office' : 'myfavplace.addofficeschool']}
            />
          )}
          {etcs.map(etc => (
            <FavoriteItem
              id={etc.id}
              key={etc.id}
              name={etc.name}
              alias={etc.alias}
              shortcutType={etc.shortcutType}
              isSaved={etc.id !== 'etc'}
              onClick={etc.id !== 'etc' ? () => onClickFindFastestPath(etc) : onClickFrequentPlaceList}
              clickCode={CLICK_CODE[etc.id !== 'etc' ? 'myfavplace.favorite' : 'myfavplace.addfavorite']}
            />
          ))}
        </FlickingArea>
      </SectionContent>
    </Section>
  )
}

export default UIFavoriteSection
